import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { 
    calculateProgress, 
    getCurrentStep, 
    getStatusColor,
    canRetryStep,
    STEP_DETAILS, 
    STEP_STATUSES,
    PIPELINE_STATUSES
} from '../../utils/pipelineHelpers';

function PipelineProgressTracker({ pipeline, onRetryStep, disabled }) {
    const { t } = useTranslation();
    if (!pipeline || !pipeline.steps) return null;

    const progress = calculateProgress(pipeline.steps);
    const currentStep = getCurrentStep(pipeline.steps);

    // Always show simplified UI - vertical progress bar with status colors, left-aligned
    return (
        <div className="max-w-md">
            {/* Vertical Step Progress Bar */}
            <div className="relative">
                {/* Vertical Progress Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200">
                    <div 
                        className={`w-full transition-all duration-500 ${
                            pipeline.status === PIPELINE_STATUSES.COMPLETED ? 'bg-green-500' :
                            pipeline.status === PIPELINE_STATUSES.FAILED ? 'bg-red-500' :
                            'bg-black'
                        }`}
                        style={{ height: `${progress}%` }}
                    />
                </div>
                
                {/* Step Circles and Labels */}
                <div className="relative space-y-8">
                    {pipeline.steps.map((step, index) => {
                        const stepInfo = STEP_DETAILS[step.step_name];
                        const isCompleted = step.status === STEP_STATUSES.COMPLETED;
                        const isProcessing = step.status === STEP_STATUSES.PROCESSING;
                        const isFailed = step.status === STEP_STATUSES.FAILED;
                        const isLastStep = index === pipeline.steps.length - 1;
                        
                        return (
                            <div key={step.id || index} className="flex items-center gap-4">
                                {/* Circle with animated ping for processing steps */}
                                <div className="relative">
                                    {/* Animated ping ring for processing steps */}
                                    {isProcessing && (
                                        <div className="absolute inset-0 w-8 h-8 rounded-full bg-black animate-ping opacity-75"></div>
                                    )}
                                    
                                    {/* Main circle */}
                                    <div className={`
                                        relative w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium flex-shrink-0
                                        ${isCompleted && isLastStep && pipeline.status === PIPELINE_STATUSES.COMPLETED ? 'bg-green-500 border-green-500 text-white' :
                                          isCompleted && isLastStep && pipeline.status === PIPELINE_STATUSES.FAILED ? 'bg-red-500 border-red-500 text-white' :
                                          isCompleted ? 'bg-black border-black text-white' : 
                                          isProcessing ? 'bg-white border-black text-black' :
                                          isFailed ? 'bg-red-500 border-red-500 text-white' :
                                          'bg-white border-gray-300 text-gray-400'}
                                    `}>
                                        {isCompleted ? '✓' : 
                                         isProcessing ? '○' :
                                         isFailed ? '×' :
                                         step.step_order || index + 1}
                                    </div>
                                </div>
                                
                                {/* Label */}
                                <div className="flex-1">
                                    <div className={`text-sm font-medium ${
                                        isCompleted || isProcessing ? 'text-black' : 
                                        isFailed ? 'text-red-600' :
                                        'text-gray-400'
                                    }`}>
                                        {t(`PipelineResults.pipelineSteps.${step.step_name}.name`) || stepInfo?.name || step.step_name}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default PipelineProgressTracker;